import { type PostHog, useFeatureFlag } from "@-/com.npmjs.posthog-react-native";
import type { HanziScript } from "@-/dev.improve.haniverse.lib.actor.types";
import { yn } from "@-/com.npmjs.yn";
import { Platform } from "@-/com.npmjs.react-native";
import { createContext, useContext } from "react";
import { getPostHog } from "#get-posthog";

export const PostHogIdentityReadyContext = createContext(false);
export const PostHogTrackingReadyContext = createContext(false);
export const PostHogFeatureFlagClientContext = createContext<PostHog | undefined>(undefined);

/**
 * Is `traditional-characters` force-enabled for this build, bypassing PostHog entirely?
 *
 * See `.env.schema` (EXPO_PUBLIC_FORCE_TRADITIONAL_CHARACTERS): the Maestro APK profile sets this
 * so the Traditional flows test the real feature instead of whatever the staging PostHog dashboard
 * currently says. Read inside the function (not at module scope) so it stays testable; Metro still
 * inlines the raw string at transform time, and `yn` accepts `"1"` or `"true"`.
 */
export function isTraditionalCharactersForced(): boolean {
  return yn(process.env.EXPO_PUBLIC_FORCE_TRADITIONAL_CHARACTERS, { default: false });
}

export function useTraditionalCharactersEnabled(): boolean {
  const client = useContext(PostHogFeatureFlagClientContext);
  const enabled = useFeatureFlag("traditional-characters", client ?? getPostHog());
  const identityReady = useContext(PostHogIdentityReadyContext);
  // The override deliberately bypasses `identityReady` too: Maestro runs as a fresh anonymous
  // guest, and `identityReady` only flips after a live `reloadFeatureFlagsAsync()` round-trip, so
  // gating on it would reintroduce exactly the network dependency this removes.
  if (isTraditionalCharactersForced()) {
    return true;
  }
  // App Store builds never switch a feature on remotely: guideline 2.5.19 rejects hidden or
  // remotely enabled features the reviewer cannot see. The Traditional curriculum ships to the
  // App Store when it is finished, as a release.
  if (Platform.OS !== "web" && process.env.EXPO_PUBLIC_APP_VARIANT === "production") {
    return false;
  }
  return identityReady && enabled === true;
}

/** Gates a preference without ever rewriting the saved setting or study data. */
export function availableHanziScript(
  preferredScript: HanziScript,
  traditionalCharactersEnabled: boolean,
): HanziScript {
  return traditionalCharactersEnabled ? preferredScript : "simplified";
}
